import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CheckCheck, MessageSquarePlus, Reply as ReplyIcon } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { ChannelIcon } from '../../../../components/ui/ChannelIcon';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { buildCommentTree, flattenCommentTree } from '../../../../lib/commentTree';
import { CHANNEL_LABEL, formatExactTimestamp, replySendArgs, type ReplyTarget } from '../../../../lib/inboxView';
import { commentBodyMessage } from '../../../../lib/inboxRowActions';
import { formatRelativeTime } from '../../../../lib/relativeTime';
import { PostGroupComment } from '../../../community/PostGroupComment';
import { PostContextHeader, PostPreviewPanel } from '../../../community/PostPreviewPanel';
import { DeleteCommentConfirm, type DeleteTarget } from './DeleteCommentConfirm';
import { socialApi } from './socialApi';

// A comment conversation for a club: the staff inbox's own post-group renderer
// (PostGroupComment), comment tree, reply-target model and large post preview,
// hosted a second time. ⚠️ Nothing here re-derives what the staff pane decides:
//   * which comment a reply or an action applies to is `commentBodyMessage`, the
//     same selector the staff pane uses, so an action can never land on a
//     different member's comment from the one on screen;
//   * which actions a channel offers is `commentModerationOffered` (inside
//     PostGroupComment), the same definition the server refuses on.
// ⚠️ DELETE IS PERMANENT ON THE PLATFORM, so it goes through a confirm that names
// the author and quotes the comment. Every platform refusal is shown VERBATIM.
// ⚠️ AI replies are ON-DEMAND: only the "Suggest a reply" button calls it.

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}

export function CommentDetail({
  threadId,
  onSelectThread,
  onChanged,
}: {
  threadId: string;
  onSelectThread: (threadId: string) => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ['social-thread', threadId], queryFn: () => socialApi.thread(threadId), retry: false });
  const thread = detail.data?.thread;
  const messages = detail.data?.messages;

  // The post's whole conversation, cached per POST so moving between its comments reuses it.
  const group = useQuery({
    queryKey: ['social-post-group', thread?.accountId, thread?.platformPostId ?? threadId],
    queryFn: () => socialApi.postGroup(threadId),
    enabled: thread !== undefined,
    refetchInterval: 30000,
    placeholderData: (prev) => prev,
    retry: false,
  });
  const nodes = useMemo(() => flattenCommentTree(buildCommentTree(group.data?.entries ?? [])), [group.data]);
  const unresolved = group.data?.unresolvedCount ?? 0;

  const [draft, setDraft] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<DeleteTarget | null>(null);
  const [targetOverride, setTargetOverride] = useState<ReplyTarget | null>(null);

  // The default target is the comment you arrived from, picked by the SAME selector
  // the row actions use. Switchable to any comment in the group, or to the post.
  const defaultTarget = useMemo<Extract<ReplyTarget, { kind: 'comment' }> | null>(() => {
    const body = commentBodyMessage(messages ?? []);
    return body && thread ? { kind: 'comment', threadId: thread.id, messageId: body.id, authorName: body.authorName } : null;
  }, [messages, thread]);
  const replyTarget: ReplyTarget = targetOverride ?? defaultTarget ?? { kind: 'post' };

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['social-thread'] });
    void qc.invalidateQueries({ queryKey: ['social-post-group'] });
    onChanged();
  };
  const fail = (e: unknown) => setFailure(errorText(e));
  const done = () => {
    setFailure(null);
    refresh();
  };

  const send = useMutation({
    mutationFn: () => {
      const args = replySendArgs(replyTarget, threadId);
      return socialApi.replyComment(args.threadId, draft.trim(), args.replyToMessageId);
    },
    onSuccess: () => {
      setDraft('');
      setSuggestions([]);
      setTargetOverride(null);
      done();
    },
    onError: fail,
  });
  type Target = { threadId: string; messageId: string };
  const like = useMutation({
    mutationFn: (v: Target & { liked: boolean }) => (v.liked ? socialApi.unlikeComment(v.threadId, v.messageId) : socialApi.likeComment(v.threadId, v.messageId)),
    onSuccess: done,
    onError: fail,
  });
  const hide = useMutation({ mutationFn: (v: Target) => socialApi.hideComment(v.threadId, v.messageId), onSuccess: done, onError: fail });
  const unhide = useMutation({ mutationFn: (v: Target) => socialApi.unhideComment(v.threadId, v.messageId), onSuccess: done, onError: fail });
  const remove = useMutation({
    mutationFn: (v: Target) => socialApi.deleteComment(v.threadId, v.messageId),
    onSuccess: () => {
      setDeleting(null);
      done();
    },
    // The platform's own sentence, verbatim; the confirm closes so the failure is seen.
    onError: (e) => {
      setDeleting(null);
      fail(e);
    },
  });
  const resolveOne = useMutation({ mutationFn: (id: string) => socialApi.setStatus(id, 'resolved'), onSuccess: done, onError: fail });
  const resolveAll = useMutation({ mutationFn: () => socialApi.resolvePost(threadId), onSuccess: done, onError: fail });
  const suggest = useMutation({
    mutationFn: () => {
      const args = replySendArgs(replyTarget, threadId);
      return socialApi.suggestReply(args.threadId, args.replyToMessageId);
    },
    onSuccess: (r) => setSuggestions(r.drafts),
    onError: fail,
  });

  if (detail.isPending) return <CenteredSpinner />;
  if (detail.isError || !thread) {
    return (
      <p className="text-sm text-destructive" role="alert">
        That conversation was not found.
      </p>
    );
  }

  return (
    <div className="@container space-y-3" data-testid="comment-detail">
      <div className="flex flex-wrap items-center gap-2">
        <ChannelIcon channel={thread.channel} />
        <span className="text-sm font-semibold text-foreground">{thread.author.name || 'A customer'}</span>
        <span className="text-xs text-muted-foreground" title={formatExactTimestamp(thread.lastActivityAt)}>
          {formatRelativeTime(thread.lastActivityAt)}
        </span>
        <span className="text-xs text-muted-foreground">{CHANNEL_LABEL[thread.channel]}</span>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${unresolved > 0 ? 'bg-tone-warning text-tone-warning-foreground' : 'bg-muted text-muted-foreground'}`}
          data-testid="unresolved-count"
          title="Comments on this post that are not resolved"
        >
          {unresolved} unresolved
        </span>
        {thread.status !== 'resolved' && (
          <Button size="sm" variant="outline" disabled={resolveOne.isPending} onClick={() => resolveOne.mutate(thread.id)} title="Resolve just this comment">
            <Check className="h-3.5 w-3.5" />
            Resolve
          </Button>
        )}
        {unresolved > 1 && (
          <Button
            size="sm"
            variant="outline"
            disabled={resolveAll.isPending}
            title={`Resolve all ${unresolved} unresolved comments on this post`}
            onClick={() => {
              if (window.confirm(`Resolve all ${unresolved} unresolved comments on this post?`)) resolveAll.mutate();
            }}
          >
            <CheckCheck className="h-3.5 w-3.5" />
            Resolve all ({unresolved})
          </Button>
        )}
      </div>

      {/* Narrow containers get the compact strip; wide ones the large panel on the right. */}
      <div className="@3xl:hidden">
        <PostContextHeader thread={thread} />
      </div>

      <div className="grid gap-4 @3xl:grid-cols-[minmax(26rem,1fr)_minmax(20rem,1.25fr)]">
        <div className="min-w-0 space-y-3">
          <div className="space-y-2" data-testid="post-group">
            {nodes.map((node) => (
              <PostGroupComment
                key={node.entry.thread.id}
                node={node}
                selected={node.entry.thread.id === thread.id}
                isReplyTarget={replyTarget.kind === 'comment' && replyTarget.threadId === node.entry.thread.id}
                onSelect={() => onSelectThread(node.entry.thread.id)}
                onReply={(m) => setTargetOverride({ kind: 'comment', threadId: node.entry.thread.id, messageId: m.id, authorName: m.authorName })}
                onResolve={() => resolveOne.mutate(node.entry.thread.id)}
                resolving={resolveOne.isPending}
                likePending={like.isPending}
                onLike={(m) => like.mutate({ threadId: node.entry.thread.id, messageId: m.id, liked: m.liked ?? false })}
                onHide={(m) => hide.mutate({ threadId: node.entry.thread.id, messageId: m.id })}
                onUnhide={(m) => unhide.mutate({ threadId: node.entry.thread.id, messageId: m.id })}
                onDelete={(m) => setDeleting({ threadId: node.entry.thread.id, messageId: m.id, authorName: m.authorName, text: m.text })}
              />
            ))}
            {nodes.length === 0 && !group.isPending && <p className="py-4 text-center text-sm text-muted-foreground">No comments on this post.</p>}
          </div>

          {deleting && (
            <DeleteCommentConfirm target={deleting} pending={remove.isPending} onConfirm={() => remove.mutate(deleting)} onCancel={() => setDeleting(null)} />
          )}

          {failure && (
            <p role="alert" className="rounded-md border-2 border-destructive px-3 py-2 text-sm text-destructive" data-testid="send-failure">
              {failure}
            </p>
          )}

          <div className="rounded-md border border-border p-3">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs" data-testid="reply-target">
              {replyTarget.kind === 'comment' ? (
                <>
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-foreground">
                    <ReplyIcon className="h-3 w-3" />
                    Replying to {replyTarget.authorName}
                  </span>
                  <button type="button" className="text-muted-foreground underline" onClick={() => setTargetOverride({ kind: 'post' })}>
                    Comment on post instead
                  </button>
                </>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-foreground">
                    <MessageSquarePlus className="h-3 w-3" />
                    New comment on the post
                  </span>
                  {defaultTarget && (
                    <button type="button" className="text-muted-foreground underline" onClick={() => setTargetOverride(null)}>
                      Reply to {defaultTarget.authorName} instead
                    </button>
                  )}
                </>
              )}
            </div>
            <Button type="button" variant="outline" size="sm" disabled={suggest.isPending || replyTarget.kind !== 'comment'} onClick={() => suggest.mutate()}>
              {suggest.isPending ? 'Thinking…' : 'Suggest a reply (AI)'}
            </Button>
            <span className="ml-2 text-xs text-muted-foreground">AI drafts are suggestions. Read them and check any facts before sending.</span>
            {suggestions.length > 0 && (
              <ul className="mt-2 space-y-2" data-testid="reply-suggestions">
                {suggestions.map((s, i) => (
                  <li key={i} className="rounded-md border border-border p-2 text-sm">
                    <p className="whitespace-pre-wrap">{s}</p>
                    <Button className="mt-1" size="sm" type="button" onClick={() => setDraft(s)}>
                      Use this one
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <textarea
              className="mt-2 w-full rounded-md border border-input bg-background p-2 text-sm text-foreground"
              rows={3}
              placeholder={replyTarget.kind === 'comment' ? `Reply to ${replyTarget.authorName}` : 'Write a new comment on the post'}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button className="mt-2" disabled={send.isPending || draft.trim() === ''} onClick={() => send.mutate()}>
              {send.isPending ? 'Sending…' : 'Send'}
            </Button>
            <span className="ml-2 text-xs text-muted-foreground">This reply is public.</span>
          </div>
        </div>

        <PostPreviewPanel thread={thread} className="hidden @3xl:sticky @3xl:top-4 @3xl:block @3xl:h-[calc(100dvh-15rem)] @3xl:self-start" />
      </div>
    </div>
  );
}
